import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { safeNext, withNext } from "@/lib/next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Header from "@/components/Header";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(safeNext());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="login-page">
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-3">Sign in</div>
            <h1 className="text-4xl md:text-5xl tracking-tighter font-black mb-6">Welcome back.</h1>
            <p className="text-[#52525B] text-base leading-relaxed max-w-md">
              Access your vector workspace, re-download past jobs and start a new
              vectorization in seconds.
            </p>
          </div>
          <div className="md:col-span-7 md:col-start-7">
            <form
              onSubmit={submit}
              className="border border-black/10 bg-[#F8F9FA] p-8 md:p-12 max-w-md ml-auto"
              data-testid="login-form"
            >
              <div className="space-y-6">
                <div>
                  <Label htmlFor="email" className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                    className="mt-2 rounded-none bg-white border-black/15 focus-visible:ring-2 focus-visible:ring-[#ED5A24] focus-visible:ring-offset-0 h-11"
                    placeholder="you@studio.com"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="mt-2 rounded-none bg-white border-black/15 focus-visible:ring-2 focus-visible:ring-[#ED5A24] focus-visible:ring-offset-0 h-11"
                    placeholder="••••••••"
                  />
                  <Link to="/forgot-password" data-testid="goto-forgot-password" className="inline-block mt-2 text-xs text-[#ED5A24] underline underline-offset-4 hover:text-[#C44619]">
                    Forgot password?
                  </Link>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="w-full rounded-none bg-[#ED5A24] hover:bg-[#C44619] text-white text-xs uppercase tracking-[0.2em] font-bold h-12"
                >
                  {loading ? "Signing in…" : "Sign in"} <ArrowRight className="w-4 h-4" />
                </Button>
                <div className="text-sm text-[#52525B] text-center">
                  No account?{" "}
                  <Link to={withNext("/register")} data-testid="goto-register" className="text-[#ED5A24] underline underline-offset-4 hover:text-[#C44619]">
                    Create one
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
